import { useEffect, useState, useCallback, useRef } from "react";
import { http, fmtErr, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import { LogOut, Bell, UtensilsCrossed, Flame } from "lucide-react";
import { toast } from "sonner";

const ORDER_NEXT = { NEW: "ACKNOWLEDGED", ACKNOWLEDGED: "PREPARING", PREPARING: "READY", READY: "DELIVERED" };
const ORDER_LABEL = { NEW: "Acknowledge", ACKNOWLEDGED: "Start Preparing", PREPARING: "Mark Ready", READY: "Mark Delivered" };
const SVC_NEXT = { REQUESTED: "ACCEPTED", ACCEPTED: "IN_PROGRESS", IN_PROGRESS: "COMPLETED" };
const SVC_LABEL = { REQUESTED: "Accept", ACCEPTED: "In Progress", IN_PROGRESS: "Mark Completed" };

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = 880; g.gain.value = 0.15;
    o.start(); setTimeout(() => { o.stop(); ctx.close(); }, 280);
  } catch (e) {
    console.warn("Audio beep unavailable:", e);
  }
}

export default function StaffDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState({ active: [], completed: [] });
  const [reqs, setReqs] = useState({ active: [], completed: [] });
  const [today, setToday] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const seen = useRef(new Set());

  const load = useCallback(async () => {
    try {
      const [o, r] = await Promise.all([http.get("/kitchen/orders"), http.get("/staff/service-requests")]);
      const pending = [
        ...o.data.active.filter((x) => x.status === "NEW").map((x) => x.id),
        ...r.data.active.filter((x) => x.status === "REQUESTED").map((x) => x.id),
      ];
      const fresh = pending.filter((id) => !seen.current.has(id));
      seen.current = new Set(pending);
      if (fresh.length && audioEnabled) beep();
      setOrders(o.data);
      setReqs(r.data);
    } catch (e) {
      console.error("Staff dashboard refresh failed:", e);
    }
  }, [audioEnabled]);

  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);
  useEffect(() => { http.get("/staff/today").then((r) => setToday(r.data)).catch(() => {}); }, []);

  const advOrder = async (o) => {
    try { await http.post(`/kitchen/orders/${o.id}/status`, { status: ORDER_NEXT[o.status] }); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const advReq = async (r) => {
    try { await http.post(`/staff/service-requests/${r.id}/status`, { status: SVC_NEXT[r.status] }); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const doLogout = async () => { await logout(); nav("/login"); };

  const newOrders = orders.active.filter((o) => o.status === "NEW");
  const newReqs = reqs.active.filter((r) => r.status === "REQUESTED");
  const inProgress = [
    ...orders.active.filter((o) => o.status !== "NEW").map((o) => ({ kind: "order", ...o })),
    ...reqs.active.filter((r) => r.status !== "REQUESTED").map((r) => ({ kind: "svc", ...r })),
  ];
  const completed = [
    ...orders.completed.map((o) => ({ kind: "order", ...o })),
    ...reqs.completed.map((r) => ({ kind: "svc", ...r })),
  ];

  return (
    <div className="min-h-screen pb-24" style={{ background: "var(--surface)" }}>
      <div className="max-w-2xl mx-auto px-4 pt-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <BrandLogo className="h-10 mb-2" />
            <div className="eyebrow mb-1">Staff Panel</div>
            <h1 className="font-serif text-3xl">Staff Dashboard</h1>
            <p className="text-sm" style={{ color: "var(--muted)" }}>{user?.name}</p>
          </div>
          <div className="flex gap-2">
            {!audioEnabled && (
              <button data-testid="staff-enable-sound" onClick={() => { setAudioEnabled(true); beep(); }} className="btn-outline" style={{ padding: "8px 12px", fontSize: 13 }}>
                <Bell size={15} className="inline mr-1" />Sound On
              </button>
            )}
            <button data-testid="staff-logout" onClick={doLogout} className="btn-outline" style={{ padding: "8px 12px", fontSize: 13 }}>
              <LogOut size={15} className="inline mr-1" />Sign Out
            </button>
          </div>
        </div>

        {today && (
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="card" style={{ padding: 14 }} data-testid="staff-current-guests">
              <div className="eyebrow mb-2">Current Guests</div>
              {today.current_guests.length === 0 && <p className="text-xs" style={{ color: "var(--muted)" }}>None in-house</p>}
              {today.current_guests.map((g) => (
                <div key={g.stay_id} className="text-sm py-1">{g.guest_name} · {g.total_pax} pax<span className="text-xs block" style={{ color: "var(--muted)" }}>out {g.check_out_date}</span></div>
              ))}
            </div>
            <div className="card" style={{ padding: 14 }} data-testid="staff-arrivals">
              <div className="eyebrow mb-2">Today's Arrivals</div>
              {today.arrivals_today.length === 0 && <p className="text-xs" style={{ color: "var(--muted)" }}>None today</p>}
              {today.arrivals_today.map((a) => (
                <div key={a.booking_reference} className="text-sm py-1">{a.guest_name}<span className="text-xs block" style={{ color: "var(--muted)" }}>{a.booking_reference}</span></div>
              ))}
            </div>
          </div>
        )}

        <Section title="New Food Orders" count={newOrders.length} testid="staff-new-orders">
          {newOrders.map((o) => (
            <div key={o.id} className="card pulse-alert" data-testid={`staff-order-${o.order_number}`} style={{ borderColor: "var(--terracotta)" }}>
              <div className="flex justify-between mb-2">
                <div className="font-serif text-xl flex items-center gap-2"><UtensilsCrossed size={18} style={{ color: "var(--terracotta)" }} />{o.order_number}</div>
                <div className="badge-terracotta">NEW</div>
              </div>
              <div className="text-sm mb-1"><b>Guest:</b> {o.guest_name}</div>
              <div className="text-sm mb-2">{o.items.map((i) => `${i.quantity}× ${i.item_name_snapshot}`).join(" · ")}</div>
              {o.special_instruction && <div className="text-sm mb-3 p-2 rounded" style={{ background: "var(--terracotta-subtle)" }}>Note: {o.special_instruction}</div>}
              <div className="text-xs mb-3" style={{ color: "var(--muted)" }}>{new Date(o.created_at).toLocaleTimeString()} · {inr(o.total)}</div>
              <button data-testid={`staff-order-advance-${o.order_number}`} onClick={() => advOrder(o)} className="btn-primary w-full">{ORDER_LABEL[o.status]}</button>
            </div>
          ))}
        </Section>

        <Section title="New Service Requests" count={newReqs.length} testid="staff-new-requests">
          {newReqs.map((r) => (
            <div key={r.id} className="card pulse-alert" data-testid={`staff-req-${r.id}`} style={{ borderColor: "var(--terracotta)" }}>
              <div className="flex justify-between mb-2">
                <div className="font-serif text-xl flex items-center gap-2"><Flame size={18} style={{ color: "var(--terracotta)" }} />{r.service_name_snapshot} × {r.quantity}</div>
                <div className="badge-terracotta">REQUESTED</div>
              </div>
              <div className="text-sm mb-1"><b>Guest:</b> {r.guest_name}</div>
              {r.special_instruction && <div className="text-sm mb-3 p-2 rounded" style={{ background: "var(--terracotta-subtle)" }}>{r.special_instruction}</div>}
              <div className="text-xs mb-3" style={{ color: "var(--muted)" }}>{new Date(r.requested_at).toLocaleTimeString()} · {inr(r.total)}</div>
              <button data-testid={`staff-req-advance-${r.id}`} onClick={() => advReq(r)} className="btn-primary w-full">{SVC_LABEL[r.status]}</button>
            </div>
          ))}
        </Section>

        <Section title="In Progress" count={inProgress.length} testid="staff-in-progress">
          {inProgress.map((x) => (
            <div key={x.id} className="card" data-testid={`staff-prog-${x.id}`}>
              <div className="flex justify-between mb-2">
                <div className="font-serif text-lg">
                  {x.kind === "order" ? x.order_number : `${x.service_name_snapshot} × ${x.quantity}`}
                </div>
                <div className="badge-pine">{x.status}</div>
              </div>
              <div className="text-sm mb-2"><b>Guest:</b> {x.guest_name}</div>
              {x.kind === "order" && <div className="text-sm mb-3">{x.items.map((i) => `${i.quantity}× ${i.item_name_snapshot}`).join(" · ")}</div>}
              <button data-testid={`staff-prog-advance-${x.id}`} onClick={() => (x.kind === "order" ? advOrder(x) : advReq(x))} className="btn-pine w-full" style={{ padding: "10px 18px", fontSize: 14 }}>
                {x.kind === "order" ? ORDER_LABEL[x.status] : SVC_LABEL[x.status]}
              </button>
            </div>
          ))}
        </Section>

        <Section title="Completed" count={completed.length} testid="staff-completed">
          {completed.map((x) => (
            <div key={x.id} className="card" style={{ opacity: 0.7 }} data-testid={`staff-done-${x.id}`}>
              <div className="flex justify-between">
                <div className="font-serif">{x.kind === "order" ? x.order_number : x.service_name_snapshot}</div>
                <div className="badge-pine">{x.status}</div>
              </div>
              <div className="text-xs" style={{ color: "var(--muted)" }}>{x.guest_name}</div>
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, count, children, testid }) {
  return (
    <div className="mb-8" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="font-serif text-2xl">{title}</h2>
        <span className="badge-pine">{count}</span>
      </div>
      <div className="space-y-3">
        {count === 0 && <p className="text-sm" style={{ color: "var(--muted)" }}>Nothing here right now.</p>}
        {children}
      </div>
    </div>
  );
}
